import React, { useState, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import TimePicker from "@components/ui/selection/TimePicker";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDisponibiliteStore, AvailabilityBlock } from "@store/disponibiliteStore";
import { useDataStore } from "@store/user_org";
import { hourToTimeStr, timeStrToHour, snapToGrid } from "@utils/timeGrid";

const HOURS_START = 8;
const HOURS_END = 20;
const SNAP_QUARTER = 0.25;

interface BlockModalProps {
  mode: "edit" | "create";
  /** Required for edit mode */
  block?: AvailabilityBlock;
  /** Required for create mode */
  workerId?: string;
  initialDate?: string;
  initialStartHour?: number;
  initialDuration?: number;
  onClose: () => void;
}

export function BlockModal({
  mode,
  block,
  workerId,
  initialDate,
  initialStartHour = HOURS_START,
  initialDuration = SNAP_QUARTER,
  onClose,
}: BlockModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const updateBlock = useDisponibiliteStore((s) => s.updateBlock);
  const removeBlock = useDisponibiliteStore((s) => s.removeBlock);
  const addBlock = useDisponibiliteStore((s) => s.addBlock);

  const isEdit = mode === "edit" && block;

  const [label, setLabel] = useState(isEdit ? block.label : "");
  const [recurring, setRecurring] = useState(isEdit ? block.recurring : false);
  const [startTime, setStartTime] = useState(
    hourToTimeStr(isEdit ? block.startHour : initialStartHour),
  );
  const [endTime, setEndTime] = useState(
    hourToTimeStr(
      isEdit
        ? block.startHour + block.durationHours
        : initialStartHour + initialDuration,
    ),
  );

  const date = isEdit ? block.date : initialDate!;

  const dayOfWeek = useMemo(() => {
    const d = new Date(date + "T00:00:00");
    const day = d.getDay();
    return day === 0 ? 6 : day - 1;
  }, [date]);

  const handleSave = () => {
    if (!label.trim()) return;
    const start = snapToGrid(timeStrToHour(startTime), SNAP_QUARTER);
    const end = snapToGrid(timeStrToHour(endTime), SNAP_QUARTER);
    const duration = end - start;
    if (duration <= 0) return;

    if (isEdit) {
      updateBlock(block.id, {
        label: label.trim(),
        startHour: start,
        durationHours: duration,
        recurring,
        dayOfWeek: recurring ? dayOfWeek : undefined,
      });
    } else {
      addBlock({
        workerId: workerId!,
        label: label.trim(),
        type: recurring ? "off" : "autre",
        date: initialDate!,
        startHour: start,
        durationHours: duration,
        recurring,
        dayOfWeek: recurring ? dayOfWeek : undefined,
      }, orgData?.id || "");
    }
    onClose();
  };

  const handleDelete = () => {
    if (isEdit) {
      removeBlock(block.id);
      onClose();
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      title={isEdit ? "Modifier la disponibilité" : "Ajouter une indisponibilité"}
      startIcon={
        <Icon1 name={isEdit ? "sett-dispo" : "plus"} size={20} />
      }
      width="max-w-600"
    >
      <div className="flex flex-col p-5" style={{ gap: 16 }}>
        <TextField
          label="Libellé"
          value={label}
          onChange={(v) => setLabel(v)}
          placeholder="Ex: OFF - Mercredi Matin"
        />

        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <TimePicker
              label="Début"
              value={startTime}
              onChange={(v) => setStartTime(v)}
              minHour={HOURS_START}
              maxHour={HOURS_END}
              minuteStep={15}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Fin"
              value={endTime}
              onChange={(v) => setEndTime(v)}
              minHour={HOURS_START}
              maxHour={HOURS_END}
              minuteStep={15}
            />
          </div>
        </div>

        <SwitchBrick
          checked={recurring}
          onChange={(v) => setRecurring(v)}
          title="Récurrent"
          description="Se répète chaque semaine"
        />

        <div
          className={`flex flex-row gap-2 pt-2 ${isEdit ? "justify-between" : "justify-end"}`}
        >
          {isEdit && (
            <SecondaryButton
              label="Supprimer"
              danger
              onClick={handleDelete}
              variant="small"
            />
          )}
          <div className="flex flex-row gap-2">
            <SecondaryButton
              label="Annuler"
              onClick={onClose}
              variant="small"
            />
            <Button
              label={isEdit ? "Enregistrer" : "Ajouter"}
              onClick={handleSave}
              variant="small"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
